/**
 * DiaryCard — **一頁日記**（日記簿嘅一頁）
 *
 * 血緣：`decisions/027`（日期喺左上角／感受標題／寫感受／下面歌詞＋播放器）
 *   ＋ `decisions/036`（**純閱讀**：大日期（左）＋頁碼（右）＋裝訂邊紙感）
 * 由 `pages/SongsPage.tsx` 拆出（`P-90`：每個檔 ≤200 行）
 *
 * ⚠️ **純顯示 ＋ 回呼**（唔自己拉 API）；播放器綁**全 app 播放器**（一個 app 只會播一首）
 * ⚠️ 歌詞用**手寫風格**（`--font-hand`；零下載 —— 唔會將私密歌詞送去第三方字型服務）
 */
import { useTranslation } from 'react-i18next';
import VariantPicker from '@coeldery/variant-picker';
import DiaryPlayer from './DiaryPlayer';
import DiaryShareActions from './DiaryShareActions';
import JournalEditor, { HAND_STYLE } from './JournalEditor';
import type { LibrarySong } from '../hooks/useLibrary';
import type { JournalSaveState } from '../hooks/useJournalAutosave';
import type { Track } from '../player/playerModel';

/** 日期（按語言）—— 左上角用 */
export function formatDate(iso: string | undefined, locale: string): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleDateString(locale === 'en' ? 'en-GB' : locale === 'zh-CN' ? 'zh-CN' : 'zh-HK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** 日記頁 props（全部係資料／回呼；**唔自己拉 API**） */

export interface DiaryCardProps {
  song: LibrarySong;
  /** 第幾頁（0-based）／總頁數 */
  index: number;
  total: number;
  locale: string;
  /** 順播 queue（撳播放時用；同畫面次序一致） */
  queue: Track[];
  deviceId: string | null;
  /** 自由書寫（自動儲存；狀態照實） */
  draft: string;
  saveState?: JournalSaveState;
  onJournalChange: (text: string) => void;
  onRetry: () => void;
  /** 揀版本（多版本時） */
  busy: boolean;
  onPick: (variantIndex: number) => void;
  /** 〔加入播放清單〕 */
  onAddPlaylist: () => void;
  /** ⭐ **改歌名**（`decisions/039`）—— 父層負責彈窗／發請求 */
  onRenameTitle: () => void;
}

/* ── 實際 render ── */
export default function DiaryCard({
  song: s,
  index,
  total,
  locale,
  queue,
  deviceId,
  draft,
  saveState,
  onJournalChange,
  onRetry,
  busy,
  onPick,
  onAddPlaylist,
  onRenameTitle,
}: DiaryCardProps) {
  const { t } = useTranslation();

  return (
    <article
      data-testid="diary-page"
      style={{
        /** ⭐ 產品負責人 2026-09-24：**似一本簿** —— 左邊裝訂邊、右邊頁邊、紙感 */
        background: 'var(--color-card)',
        border: '1px solid var(--color-divider)',
        borderLeft: '6px solid var(--color-brand, var(--color-primary))',
        borderRadius: '4px 16px 16px 4px',
        padding: '18px 18px 20px',
        boxShadow: 'var(--shadow-soft, none)',
        backgroundImage:
          'repeating-linear-gradient(90deg, color-mix(in srgb, var(--color-divider) 22%, transparent), color-mix(in srgb, var(--color-divider) 22%, transparent) 1px, transparent 1px, transparent 6px)',
      }}
    >
      {/* 頂：**大日期**（左）＋ 頁碼（右）—— 似揭書（設計 S8：`.diary-page__date`） */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' }}>
        <p data-testid="diary-date" className="diary-page__date" style={{ margin: 0, textAlign: 'left' }}>
          {formatDate(s.createdAt, locale)}
        </p>
        <span data-testid="diary-page-no" className="footnote" style={{ whiteSpace: 'nowrap' }}>
          {t('diary_page_of', { n: index + 1, total })}
        </span>
      </div>

      {/* 感受標題 ＝ 歌名（＋細 ✎ 改歌名；`decisions/039`；設計 `.diary-page__titlerow`） */}
      <div className="diary-page__titlerow" style={{ margin: '6px 0 12px' }}>
        <h2
          data-testid="diary-title"
          style={{ margin: 0, flex: 1, minWidth: 0, fontSize: 'var(--font-size-title, 22px)', textAlign: 'left' }}
        >
          {s.title}
        </h2>
        <button
          type="button"
          data-testid="diary-title-edit"
          aria-label={t('song_rename')}
          title={t('song_rename')}
          onClick={onRenameTitle}
          style={{
            minWidth: '36px',
            minHeight: '36px',
            borderRadius: '999px',
            border: '1.5px solid var(--color-divider)',
            background: 'var(--color-card)',
            color: 'var(--color-text)',
            fontFamily: 'inherit',
            fontSize: '16px',
            cursor: 'pointer',
            flex: '0 0 auto',
          }}
        >
          ✎
        </button>
      </div>

      {/* 自由書寫（自動儲存；狀態照實） */}
      <JournalEditor
        songId={s.id}
        draft={draft}
        saveState={saveState}
        onChange={onJournalChange}
        onRetry={onRetry}
      />

      {/* 播放器（綁全 app 播放器；`DiaryPlayer`） */}
      <div style={{ marginTop: '14px' }}>
        <DiaryPlayer song={s} queue={queue} deviceId={deviceId} />
      </div>

      {/* 多版本：一次回 2 個（已收費）→ 試聽再揀（撳卡揀＋高亮；`variant-picker` 1.1.0） */}
      {s.status === 'succeeded' && (s.variants?.length ?? 0) > 1 ? (
        <VariantPicker
          variants={s.variants ?? []}
          pickedIndex={s.pickedIndex ?? 0}
          busy={busy}
          onPick={onPick}
          audioSrcFor={(i) => `/api/song/audio/${s.id}?deviceId=${encodeURIComponent(deviceId ?? '')}&variant=${i}`}
          /** ⭐ 2026-09-23#6（產品負責人）：「sharing it out should only attach the one we pick」
           *   → **日記頁唔再逐版出分享掣**：只有下面一個〔分享整頁〕，分享頁播**揀咗嗰版** */
        />
      ) : null}

      {/* 歌詞（手寫風格） */}
      <details open>
        <summary style={{ cursor: 'pointer', fontSize: 'var(--font-size-small, 15px)', fontWeight: 700 }}>
          {t('diary_lyrics_title')}
        </summary>
        <p data-testid="diary-lyrics" style={{ ...HAND_STYLE, margin: '10px 0 0', whiteSpace: 'pre-wrap', padding: '2px 10px 6px' }}>
          {s.lyrics}
        </p>
      </details>

      {/* ⭐ 2026-09-23（`decisions/031`）：**分享整頁** ＋ 〔加入播放清單〕（`DiaryShareActions`） */}
      <DiaryShareActions
        songId={s.id}
        title={s.title ?? ''}
        deviceId={deviceId}
        variantLabel={
          (s.variants?.length ?? 0) > 1
            ? t('share_page_version', { n: (s.pickedIndex ?? 0) + 1, total: s.variants?.length ?? 1 })
            : undefined
        }
        onAddPlaylist={onAddPlaylist}
      />
    </article>
  );
}

